import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';

export interface EditInterestsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { interests: string[] }) => Promise<void>;
  initialData: string[];
}

export const EditInterestsModal: React.FC<EditInterestsModalProps> = ({ open, onClose, onSave, initialData }) => {
  const { t } = useI18n();
  const [selected, setSelected] = useState<string[]>(initialData || []);
  const [search, setSearch] = useState('');
  const [allInterests, setAllInterests] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(initialData || []);
  }, [initialData, open]);

  useEffect(() => {
    const fetchInterests = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('hobbies')
        .select('name')
        .order('name', { ascending: true });
      if (!error && data) {
        setAllInterests(data.map((item: any) => item.name));
      }
      setLoading(false);
    };
    fetchInterests();
  }, []);

  const getHobbyTranslation = (hobbyName: string) => {
    // Mapeamento de nomes do banco para chaves de tradução
    const hobbyMapping: { [key: string]: string } = {
      'Reading': 'reading',
      'Hiking / Trekking': 'hiking_trekking',
      'Photography': 'photography',
      'Gaming (Video Games / Board Games)': 'gaming',
      'Cooking / Baking': 'cooking_baking',
      'Traveling': 'traveling',
      'Cycling (Road / Mountain)': 'cycling',
      'Running': 'running',
      'Weightlifting / Fitness': 'weightlifting_fitness',
      'Yoga / Pilates': 'yoga_pilates',
      'Swimming': 'swimming',
      'Surfing / Paddleboarding': 'surfing_paddleboarding',
      'Fishing': 'fishing',
      'Gardening': 'gardening',
      'Music (Playing an instrument / Listening)': 'music',
      'Podcasts': 'podcasts',
      'Movies / TV Shows': 'movies_tv_shows',
      'Drawing / Painting': 'drawing_painting',
      'Writing': 'writing',
      'Learning Languages': 'learning_languages',
      'Volunteering': 'volunteering',
      'Meditation': 'meditation',
      'Camping': 'camping',
      'Motorcycling': 'motorcycling',
      'Cars / Motorsports': 'cars_motorsports',
      'Collecting (e.g., cards, coins, stamps)': 'collecting',
      'DIY / Home Improvement': 'diy_home_improvement',
      'Chess / Strategy Games': 'chess_strategy_games',
      'Martial Arts (other than Jiu-Jitsu)': 'martial_arts',
      'Team Sports (e.g., Soccer, Basketball, Volleyball)': 'team_sports',
      'Golf': 'golf',
      'Tennis': 'tennis',
      'Skiing / Snowboarding': 'skiing_snowboarding',
      'Table Tennis / Ping Pong': 'table_tennis_ping_pong',
      'Bowling': 'bowling',
      'Dancing': 'dancing',
      'Fashion / Style': 'fashion_style',
      'Astrology / Spirituality': 'astrology_spirituality',
      'Podcasting / Content Creation': 'podcasting_content_creation',
      'Woodworking': 'woodworking',
      'Knitting / Crocheting': 'knitting_crocheting',
      'Calligraphy': 'calligraphy',
      'Coding / Programming (as a hobby)': 'coding_programming',
      'Brewing (Beer / Coffee)': 'brewing',
      'Astronomy': 'astronomy',
      'Birdwatching': 'birdwatching',
      'Archery': 'archery',
      'Hunting': 'hunting',
      'Puzzles': 'puzzles',
      'Other': 'other'
    };

    const mappedKey = hobbyMapping[hobbyName];
    if (mappedKey) {
      return t(`profile.hobbies.${mappedKey}`) || hobbyName;
    }

    // Fallback: tentar converter o nome diretamente
    const hobbyKey = hobbyName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    return t(`profile.hobbies.${hobbyKey}`) || hobbyName;
  };

  const filtered = allInterests.filter(
    (i) => i.toLowerCase().includes(search.toLowerCase()) && !selected.includes(i)
  );

  const handleAdd = (interest: string) => {
    if (!selected.includes(interest) && selected.length < 5) {
      setSelected([...selected, interest]);
      setSearch('');
    }
  };

  const handleRemove = (interest: string) => {
    setSelected(selected.filter((i) => i !== interest));
  };

  const maxInterests = 5;
  const reachedMax = selected.length >= maxInterests;

  const handleSave = async () => {
    setSaving(true);
    await onSave({ interests: selected });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-black font-sans">{t('profile.edit_interests')}</DialogTitle>
          <DialogDescription className="text-gray-600 text-sm">
            {t('profile.edit_interests_description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.search_add_interests')}</label>
            <Input
              placeholder={loading ? t('profile.loading_interests') : t('profile.search_add_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-2 bg-white text-black placeholder:text-[#b0b0b0] border-black font-sans"
              disabled={reachedMax || loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && search.trim() && !reachedMax) {
                  handleAdd(search.trim());
                }
              }}
            />
            {search && filtered.length > 0 && !reachedMax && !loading && (
              <div className="border rounded bg-white p-2 mb-2 max-h-32 overflow-y-auto">
                {filtered.map((interest) => (
                  <div
                    key={interest}
                    className="cursor-pointer px-2 py-1 hover:bg-[#D72E16] hover:text-white rounded font-sans text-black"
                    onClick={() => handleAdd(interest)}
                  >
                    {getHobbyTranslation(interest)}
                  </div>
                ))}
              </div>
            )}
            {search && filtered.length === 0 && !reachedMax && !loading && (
              <div className="border rounded bg-white p-2 mb-2">
                <div
                  className="cursor-pointer px-2 py-1 hover:bg-[#D72E16] hover:text-white rounded font-sans text-black"
                  onClick={() => handleAdd(search.trim())}
                >
                  {t('profile.add_interest', { interest: search.trim() })}
                </div>
              </div>
            )}
            <div className="mb-4">
              <div className="font-medium mb-1 text-black font-sans">{t('profile.interests_selected')}</div>
              <div className="flex flex-wrap gap-2 mb-2">
                {selected.map((interest) => (
                  <span
                    key={interest}
                    className="inline-flex items-center rounded-full bg-red-100 text-red-700 border border-red-200 px-4 py-1 text-xs font-medium font-sans mr-2 mb-2"
                  >
                    {getHobbyTranslation(interest)}
                    <button
                      type="button"
                      onClick={() => handleRemove(interest)}
                      className="ml-2 text-xs text-red-400 hover:text-red-700 focus:outline-none"
                      aria-label={`Remove ${interest}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="text-xs text-[#b0b0b0] mt-1 font-sans">
                {t('profile.interests_count', { count: selected.length, max: maxInterests })}
              </div>
              {reachedMax && (
                <div className="text-xs text-red-500 mt-1 font-sans">{t('profile.max_interests_reached')}</div>
              )}
            </div>
          </div>
        </div>
        <Button
          className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-3 h-11 rounded-full transition-colors duration-200 disabled:opacity-50 mt-6"
          onClick={handleSave}
          disabled={saving || selected.length === 0}
        >
          {saving ? t('profile.saving') : t('common.save')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}; 